/**
 * KISSED University · Program Detail (from quiz / browse).
 * Route: /university/program/:key   (key ∈ L1..L4)
 *
 * Pulls program metadata from `quizData.PROGRAMS` and links to the existing
 * /university/apply route with `pathway_source=admissions_quiz` if the user
 * came from the quiz (recommendation state persists in sessionStorage).
 */
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CheckCircle2, Clock } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { PROGRAMS, QUIZ_TO_CATALOG_PRIMARY, readQuizState } from "./quizData";
import PathwayDiagram from "@/components/PathwayDiagram";

// 2026-07-06 · Program-detail URL alias map.
// Public URLs use short lowercase keys (l1, l2, l3, dts, herbal, hhfw, cct).
// The legacy PROGRAMS dict is keyed L1..L4; Wave 2's expanded catalog
// (Herbal Foundations/Practitioner, HHFW, CCT) does NOT yet have rich
// marketing content authored here — those slugs fall through to a
// data-driven "coming soon" fallback (pattern-matches the earlier DTS fix)
// that renders the actual Wave 2 kuhas_programs record instead of a 404.
const URL_ALIAS_TO_PROGRAMS_KEY = {
  l1: "L1",
  l2: "L2",
  l3: "L3",
  l4: "L4",
  dts: "L4",
};

// Wave 2 canonical DB slug (kuhas_programs.slug) for every short URL key.
// Used by the "coming soon" fallback to fetch real pricing/metadata from
// the Wave 2 API, so a data gap never renders as a "not found" 404.
const URL_KEY_TO_KUHAS_SLUG = {
  l1:     "level_1_scalp_scientist",
  l2:     "level_2_associate_tri",
  l3:     "level_3_holistic_practitioner",
  dts:    "dts",
  herbal:     "herbal_foundations",     // ambiguous — prefer Foundations for /herbal
  "herbal-foundations":  "herbal_foundations",
  "herbal-practitioner": "herbal_practitioner",
  hhfw:   "holistic_functional_wellness",
  cct:    "cct",
};

const API = process.env.REACT_APP_BACKEND_URL;

export default function UniversityProgramDetail() {
  const { key: urlKey } = useParams();
  const navigate = useNavigate();
  const [comingSoonProgram, setComingSoonProgram] = useState(null);
  const [loadingFallback, setLoadingFallback] = useState(false);

  const rawKey = String(urlKey || "");
  const lower = rawKey.toLowerCase();

  // 1. Legacy PROGRAMS lookup — case-insensitive via alias map, then direct,
  //    then slug-field match on the value objects.
  const programsKey =
    URL_ALIAS_TO_PROGRAMS_KEY[lower] ||
    (PROGRAMS[rawKey] ? rawKey : null) ||
    Object.keys(PROGRAMS).find((k) => k.toLowerCase() === lower);
  const program = programsKey ? PROGRAMS[programsKey] : null;

  // 2. Fallback — if no legacy PROGRAMS entry BUT the short URL key maps to
  //    a real Wave 2 kuhas_programs slug, fetch minimal metadata and render
  //    the "coming soon" card instead of a bare 404.
  const kuhasSlug = URL_KEY_TO_KUHAS_SLUG[lower];
  useEffect(() => {
    if (program || !kuhasSlug) return;
    setLoadingFallback(true);
    fetch(`${API}/api/kuhas/programs`)
      .then((r) => r.json())
      .then((body) => {
        const hit = (body.items || []).find((p) => p.slug === kuhasSlug);
        setComingSoonProgram(hit || null);
      })
      .catch(() => setComingSoonProgram(null))
      .finally(() => setLoadingFallback(false));
  }, [program, kuhasSlug]);

  // ── Not-found handler — reached only when the URL matches NEITHER a
  //    legacy PROGRAMS key NOR a Wave 2 kuhas_programs slug. This should
  //    now be genuinely rare (typos, dead links).
  if (!program && !kuhasSlug) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="detail-not-found">
        <div className="max-w-md text-center">
          <h2 className="font-display text-2xl text-slate-900">Program not found</h2>
          <p className="mt-2 text-sm text-slate-600">We couldn&apos;t load that program. Try the quiz or browse the catalog.</p>
          <div className="mt-4 flex gap-2 justify-center">
            <Button onClick={() => navigate("/university/quiz")} className="bg-purple-900 text-white">Take the quiz</Button>
            <Link to="/university"><Button variant="outline">Back to KISSED University</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  // ── "Coming soon" fallback for URLs that map to a real Wave 2 program
  //    slug but have no rich marketing content authored yet. Mirrors the
  //    DTS not-yet-available fallback pattern.
  if (!program) {
    return (
      <div className="min-h-screen bg-[#faf7f5]" data-testid="detail-coming-soon">
        <header className="border-b border-slate-200 bg-white">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/university" className="flex items-center gap-3">
              <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
              <span className="font-display text-sm text-slate-700">KISSED University · Program Detail</span>
            </Link>
          </div>
        </header>
        <section className="bg-gradient-to-br from-purple-100/60 via-white to-purple-50/40 border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-6 py-10">
            <Link to="/university" className="text-xs text-slate-600 inline-flex items-center hover:text-purple-900">
              <ChevronLeft className="w-3 h-3 mr-1" /> Back
            </Link>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-purple-900 font-semibold">KISSED University for Holistic &amp; Applied Sciences</div>
              <h1 className="font-display text-3xl sm:text-4xl text-slate-900 leading-tight" data-testid="detail-title">
                {loadingFallback ? "Loading…" : (comingSoonProgram?.title || "Program details coming soon")}
              </h1>
              {comingSoonProgram?.level_label && (
                <div className="mt-2 text-sm text-slate-600">{comingSoonProgram.level_label}</div>
              )}
            </div>
          </div>
        </section>
        <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex gap-3">
            <Clock className="w-5 h-5 text-amber-800 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-amber-900">Full program details launching soon</div>
              <p className="mt-1 text-sm text-amber-900/90">
                This program is part of the active KUHAS catalog and enrollment infrastructure is live.
                Full curriculum, faculty, and course-by-course detail are still being finalized. Join the
                interest list or reach out to admissions to learn more.
              </p>
            </div>
          </div>
          {comingSoonProgram && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
              <Rule label="At a glance" />
              <div className="grid sm:grid-cols-2 gap-3">
                {comingSoonProgram.num_courses > 0 && (
                  <Cell label="Total Courses" value={`${comingSoonProgram.num_courses} Courses`} />
                )}
                {comingSoonProgram.total_clock_hours && (
                  <Cell label="Clock Hours" value={`${comingSoonProgram.total_clock_hours} Hours`} />
                )}
                {comingSoonProgram.billing_model === "per_credit" ? (
                  <Cell label="Tuition" value={`$${comingSoonProgram.per_credit_rate}/credit hour`} />
                ) : comingSoonProgram.pay_in_full_price ? (
                  <Cell label="Tuition (Pay in Full)" value={`$${comingSoonProgram.pay_in_full_price.toLocaleString()}`} />
                ) : null}
                {comingSoonProgram.per_course_price && (
                  <Cell label="Per Course" value={`$${comingSoonProgram.per_course_price}`} />
                )}
              </div>
              {comingSoonProgram.requires_application && (
                <div className="text-xs text-slate-600 border-t border-slate-100 pt-3">
                  Formal application required — a non-refundable $50 application fee applies.
                </div>
              )}
            </div>
          )}
          <div className="space-y-3">
            {comingSoonProgram?.requires_application ? (
              <Link to="/university/apply" data-testid="coming-soon-apply">
                <Button size="lg" className="w-full bg-purple-900 hover:bg-purple-800 text-white rounded-full">
                  Start Application <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <Link to="/university" data-testid="coming-soon-back">
                <Button variant="outline" className="w-full rounded-full">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back to All Programs
                </Button>
              </Link>
            )}
          </div>
        </main>
      </div>
    );
  }

  const quizState = readQuizState();
  const pathwaySource = quizState ? "admissions_quiz" : "catalog_browse";
  const startMonth = quizState?.desired_start_month || "";
  const params = new URLSearchParams();
  params.set("pathway_source", pathwaySource);
  params.set("quiz_key", program.key);
  params.set("primary_program", QUIZ_TO_CATALOG_PRIMARY[program.key] || "");
  params.set("quiz_recommended_program", `${program.level} — ${program.title}`);
  if (startMonth) params.set("desired_start_month", startMonth);
  const applyHref = `/university/apply?${params.toString()}`;

  const backHref = quizState ? "/university/recommendation" : "/university";

  return (
    <div data-testid="university-program-detail" className="min-h-screen bg-[#faf7f5]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/university" className="flex items-center gap-3">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
            <span className="font-display text-sm text-slate-700">KISSED University · Program Detail</span>
          </Link>
        </div>
      </header>

      <section className="bg-gradient-to-br from-purple-100/60 via-white to-purple-50/40 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <Link to={backHref} className="text-xs text-slate-600 inline-flex items-center hover:text-purple-900" data-testid="detail-back-link">
            <ChevronLeft className="w-3 h-3 mr-1" /> Back
          </Link>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-purple-900 font-semibold">{program.college}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mt-1 mb-1">{program.badge}</div>
            <h1 className="font-display text-3xl sm:text-4xl text-slate-900 leading-tight" data-testid="detail-title">
              {program.title}
            </h1>
          </div>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-base text-slate-700 leading-relaxed mb-6" data-testid="detail-overview">{program.description}</p>

        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          <Cell label="Clock Hours" value={`${program.hours} Hours`} />
          <Cell label="Tuition" value={program.tuition} />
          <Cell label="Format" value={program.hours === "300" ? "Online" : "Hybrid"} />
          <Cell label="Total Courses" value={`${program.displayCourseCount || program.courses.length} Courses`} />
        </div>

        <Rule label="Curriculum" />
        <p className="text-xs text-slate-500 mb-3">All courses required for program completion.</p>
        <div className="space-y-2 mb-8" data-testid="detail-courses">
          {program.courses.map((c) => (
            <div key={c.code} className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-purple-900 font-semibold">{c.code}</div>
                <div className="text-sm font-medium text-slate-900">{c.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{c.hours} Clock Hours</div>
              </div>
              <div className="text-sm font-semibold text-purple-900 shrink-0">{c.price}</div>
            </div>
          ))}
        </div>

        <Rule label="Tuition & Payment" />
        <div className="rounded-2xl border border-purple-200 bg-purple-50/40 p-6 mb-8">
          {program.perCourse ? (
            <>
              <div className="text-[10px] uppercase tracking-wide text-purple-900 font-semibold">Option 1 — Pay Per Course</div>
              <div className="font-display text-3xl text-slate-900">{program.perCourse}</div>
              <div className="text-xs text-slate-600 mt-1">Register and pay course by course</div>
              <div className="h-px bg-purple-200 my-4" />
              <div className="text-[10px] uppercase tracking-wide text-purple-900 font-semibold">Option 2 — Pay in Full (Best Value)</div>
              <div className="font-display text-3xl text-slate-900">{program.tuition}</div>
              <div className="text-xs text-slate-600 mt-1">One investment — all coursework included</div>
              {program.savings && (
                <span className="inline-block mt-3 rounded-full bg-emerald-700 text-white text-[11px] font-medium px-3 py-1">
                  {program.savings}
                </span>
              )}
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-wide text-purple-900 font-semibold">Doctoral Tuition</div>
              <div className="font-display text-xl text-slate-900">By Admissions Consultation</div>
              <div className="text-xs text-slate-600 mt-2">Apply now to receive a complete tuition schedule from our admissions team.</div>
            </>
          )}
        </div>

        <Rule label="Graduation Requirements" />
        <ol className="space-y-2 mb-8" data-testid="detail-grad">
          {program.grad.map((line, i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-700">
              <span className="w-6 h-6 rounded-full bg-purple-900 text-white text-[11px] font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
              <span>{line}</span>
            </li>
          ))}
        </ol>

        <Rule label="Format" />
        <p className="text-sm text-slate-700 leading-relaxed mb-8 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
          <span>{program.format}</span>
        </p>

        <div className="space-y-3">
          <Link to={applyHref} data-testid="detail-apply-btn">
            <Button size="lg" className="w-full bg-purple-900 hover:bg-purple-800 text-white rounded-full">
              {program.requires_application !== false ? "Apply First — $50 Application Fee" : "Continue Enrollment"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          {quizState ? (
            <Link to="/university/recommendation" data-testid="detail-back-to-rec">
              <Button variant="outline" className="w-full rounded-full">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to My Recommendation
              </Button>
            </Link>
          ) : (
            <Link to="/university" data-testid="detail-back-to-landing">
              <Button variant="outline" className="w-full rounded-full">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to All Programs
              </Button>
            </Link>
          )}
        </div>

        {/* Visual pathway — auto-highlight based on program key (L1/L2/L3/L4) */}
        <div className="mt-12">
          <PathwayDiagram highlight={program.badge && program.badge.startsWith("Level ") ? `L${program.badge.split(" ").pop()}` : undefined} compact />
        </div>
      </main>
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="text-[10px] uppercase tracking-wide text-purple-900 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function Rule({ label }) {
  return (
    <div className="flex items-center gap-3 my-5">
      <span className="text-[10px] uppercase tracking-[0.18em] text-purple-900 font-semibold whitespace-nowrap">
        {label}
      </span>
      <span className="flex-1 h-px bg-slate-200" />
    </div>
  );
}
